import React, { useEffect, useState } from "react";
import moment from "moment";
import { Link, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import FrontLayout from "../../../components/layout-new-theme/FrontLayout";
import SkeletonLoading from "../booking-detail/_components/SkeletonLoading";
import { BackIcon } from "../../../components/icons";
import { cn } from "../../../utils/classnames";
import { createTypographyHelper } from "../../../utils/typography";
import { newGetBookings } from "../../../actions/auth/booking";
import CancellationFeeInformation from "./components/CancellationFeeInformation";
import CancellationPolicyModal from "../booking-edit/components/BookingEditReview/components/CancellationPolicyModal";

const STATUS = {
  PENDING_APPROVAL: "PENDING_APPROVAL",
  REJECTED: "REJECTED",
  APPROVED: "APPROVED",
  UPDATED: "UPDATED",
};


const STATUS_STYLES = {
  PENDING_APPROVAL: {
    labels: {
      en: "Change Requested",
      ja: "変更依頼中",
    },
    className: "tw:bg-[#20639B1A] tw:text-[#20639B]",
  },
  REJECTED: {
    labels: {
      en: "Change Rejected",
      ja: "変更手配不可",
    },
    className: "tw:bg-[#FFEAE7] tw:text-[#EA4335]",
  },
  APPROVED: {
    labels: {
      en: "Change Approved",
      ja: "変更承認済み",
    },
    className: "tw:bg-[#DBF4F1] tw:text-[#18B1A3]",
  },
  UPDATED: {
    labels: {
      en: "Updated",
      ja: "更新済み",
    },
    className: "tw:bg-[#FFF8E8] tw:text-[#A3780F]",
  },
};

const getStatusMeta = (status, responseBy) => {
  return status === STATUS.APPROVED && !responseBy ? STATUS_STYLES.UPDATED : STATUS_STYLES[String(status || "").toUpperCase()];
};

const getLocationDetail = (loc) => {
  if (!loc?.formatted_address && !loc?.label) {
    return { name: "-", address: "" };
  }

  if (loc?.formatted_address) {
    const formattedAddressArr = loc.formatted_address.split(",");
    const name = formattedAddressArr?.[0] || "-";
    const address = formattedAddressArr?.slice(1).join(",").trim();
    return { name, address };
  }

  return { name: loc?.label || "-", address: "" };
};

const formatDurationText = (duration, t) => {
  if (!duration) return "-";
  const [h = "0", m = "0"] = String(duration).split(":");
  const totalMinutes = Number(h) * 60 + Number(m);
  const roundedHours = Math.ceil((totalMinutes / 60) * 2) / 2;
  return `${roundedHours}${t("editBooking.BookingReviewPage.generalInformation.hours")}`;
};

const formatBilledHoursText = (minutes, t) => {
  if (minutes === null || minutes === undefined || Number.isNaN(Number(minutes))) return "-";
  const roundedHours = Math.ceil((Number(minutes) / 60) * 2) / 2;
  return `${roundedHours}${t("editBooking.BookingReviewPage.generalInformation.hours")}`;
};

const formatDateTime = (dateValue, language, options = {}) => {
  if (!dateValue) return "-";
  const { withWeekday = true } = options;
  const normalizedLanguage = String(language || "").toLowerCase();
  const isEnglish = normalizedLanguage.startsWith("en");
  const localizedMoment = moment(dateValue).locale(isEnglish ? "en" : "ja");

  if (isEnglish) {
    const month = localizedMoment.format("MMM");
    const dayWithOrdinal = localizedMoment.format("Do");
    const year = localizedMoment.format("YYYY");
    const time = localizedMoment.format("H:mm");

    if (withWeekday) {
      const weekday = localizedMoment.format("ddd").toUpperCase();
      return `${month}. ${dayWithOrdinal}, ${year} (${weekday}) ${time}`;
    }

    return `${month}. ${dayWithOrdinal}, ${year} ${time}`;
  }

  return localizedMoment.format(withWeekday ? "YYYY年MM月DD日（ddd）H:mm" : "YYYY年MM月DD日 H:mm");
};

const formatDateTimeLocal = (dateValue, language, options = {}) => {
  if (!dateValue) return "-";
  const { withWeekday = true } = options;
  const normalizedLanguage = String(language || "").toLowerCase();
  const isEnglish = normalizedLanguage.startsWith("en");
  const localizedMoment = moment(dateValue).local().locale(isEnglish ? "en" : "ja");

  if (isEnglish) {
    const month = localizedMoment.format("MMM");
    const dayWithOrdinal = localizedMoment.format("Do");
    const year = localizedMoment.format("YYYY");
    const time = localizedMoment.format("H:mm");

    if (withWeekday) {
      const weekday = localizedMoment.format("ddd").toUpperCase();
      return `${month}. ${dayWithOrdinal}, ${year} (${weekday}) ${time}`;
    }

    return `${month}. ${dayWithOrdinal}, ${year} ${time}`;
  }

  return localizedMoment.format(withWeekday ? "YYYY年MM月DD日（ddd）H:mm" : "YYYY年MM月DD日 H:mm");
};

const formatJPY = (value, t) => {
  if (value === null || value === undefined || Number.isNaN(Number(value)) || Number(value) === 0) return "-";
  return `${Number(value).toLocaleString()}${t("editBooking.BookingReviewPage.jpy")}`;
};

const formatPaxGroup = (paxList = [], t) => {
  if (!Array.isArray(paxList)) return "-";
  const text = paxList
    .filter((item) => Number(item?.paxcount || 0) > 0)
    .map((item) => `${t(`my_page.bookings_page.booking_item.${item?.paxtype}`, { defaultValue: item?.paxtype || "-" })}: ${item?.paxcount}`)
    .join(", ");
  return text || "-";
};

const formatLuggageGroup = (luggageList = [], t) => {
  if (!Array.isArray(luggageList)) return "-";
  const text = luggageList
    .filter((item) => Number(item?.luggagecount || 0) > 0)
    .map((item) => {
      const typeKey = String(item?.luggagetype || "").toLowerCase().includes("check") ? "check_in" : "cabin";
      const type = t(`my_page.bookings_page.booking_item.${typeKey}`);
      return `${type}: ${item?.luggagecount}`;
    })
    .join(", ");
  return text || "-";
};

const normalizeMessage = (message) => {
  if (!message) return "-";
  if (Array.isArray(message)) return message?.[0]?.note || "-";
  return String(message).trim() || "-";
};

const normalizeLocationValue = (loc) => {
  if (!loc) return null;
  return {
    value: loc?.value || "",
    formatted_address: loc?.formatted_address || "",
    label: loc?.label || "",
    lat: loc?.lat ?? null,
    lon: loc?.lon ?? null,
  };
};

const normalizeVehicleIdentity = (vehicle) => {
  if (!vehicle) return null;
  const vehicleTypeId =
    typeof vehicle?.vehicleTypeID === "object"
      ? vehicle?.vehicleTypeID?._id || ""
      : vehicle?.vehicleTypeID || "";

  return {
    vehicleTypeName: vehicle?.vehicleTypeName || vehicle?.vehicleTypeID?.typename || "",
    vehicleTypeID: vehicleTypeId,
  };
};

const normalizeVehiclePricing = (vehicle) => {
  if (!vehicle) return null;

  return {
    total: Number(vehicle?.total || 0),
    tollFare: Number(vehicle?.tollFare || 0),
    travelAreaPrice: Number(vehicle?.travelAreaPrice || 0),
    nightPrice: Number(vehicle?.nightPrice || 0),
    longDistanceSurcharge: Number(vehicle?.longDistanceSurcharge || 0),
    childSeatPrice: Number(vehicle?.childSeatPrice || 0),
    baseprice: Number(vehicle?.baseprice || 0),
    hourlyprice: Number(vehicle?.hourlyprice || 0),
    discount: Number(vehicle?.discount?.rate || 0),
    totalDiscount: Number(vehicle?.discount?.amount || 0),
  };
};

const normalizeVehicleAddon = (vehicle) => {
  if (!vehicle) return [];
  const seats = Array.isArray(vehicle?.childSeat) ? vehicle.childSeat : [];
  return seats
    .map((seat) => ({
      type: seat?.childSeatType || "",
      count: Number(seat?.childSeatCount || 0),
    }))
    .filter((seat) => seat.count > 0);
};

const stripMetaFields = (value) => {
  if (Array.isArray(value)) {
    return value.map(stripMetaFields);
  }

  if (value && typeof value === "object") {
    const next = {};
    Object.keys(value).forEach((key) => {
      if (["_id", "__v", "createdAt", "updatedAt", "date"].includes(key)) return;
      next[key] = stripMetaFields(value[key]);
    });
    return next;
  }

  return value;
};

const isEqualValue = (left, right) => {
  try {
    return JSON.stringify(stripMetaFields(left)) === JSON.stringify(stripMetaFields(right));
  } catch (_) {
    return left === right;
  }
};

const isChangedByKey = (key, oldRaw, newRaw) => {
  if (key === "pickupLocation") {
    return !isEqualValue(normalizeLocationValue(oldRaw), normalizeLocationValue(newRaw));
  }

  if (key === "destinations") {
    const oldNorm = Array.isArray(oldRaw) ? oldRaw.map(normalizeLocationValue) : [];
    const newNorm = Array.isArray(newRaw) ? newRaw.map(normalizeLocationValue) : [];
    return !isEqualValue(oldNorm, newNorm);
  }

  if (key === "bookingInfo") {
    const oldNorm = Array.isArray(oldRaw) ? oldRaw.map(normalizeVehicleIdentity) : [];
    const newNorm = Array.isArray(newRaw) ? newRaw.map(normalizeVehicleIdentity) : [];
    return !isEqualValue(oldNorm, newNorm);
  }

  return !isEqualValue(oldRaw, newRaw);
};

const SectionTitleRow = ({ title }) => (
  <div className="tw:flex tw:h-[32px] tw:w-full tw:items-center tw:justify-center tw:border-l tw:border-r tw:border-b tw:border-[#E8E8E8] tw:bg-[#F9F9F9]">
    <div className="tw:text-[14px] tw:leading-[18px] tw:font-[400] tw:text-[#A7A7A7]">{title}</div>
  </div>
);

const LabelCell = ({ label, first = false }) => (
  <div
    className={cn(
      "tw:w-[90px] tw:sm:w-[225px] tw:px-2 tw:sm:px-5 tw:py-4 tw:flex tw:items-center tw:border-l tw:border-b tw:border-[#E8E8E8] tw:bg-white",
      first && "tw:rounded-bl-[5px]"
    )}
  >
    <div className="tw:text-[12px] tw:sm:text-[14px] tw:leading-[18px] tw:sm:leading-[20px] tw:font-[400] tw:text-[#A7A7A7]">{label}</div>
  </div>
);

const ValueCell = ({ children, changed = false, right = false, muted = false, last = false }) => (
  <div
    className={cn(
      "tw:flex-1 tw:px-2 tw:sm:px-5 tw:py-4 tw:border-l tw:border-b tw:border-[#E8E8E8]",
      muted ? "tw:bg-[#F3F3F3]" : changed ? "tw:bg-[#FFF8E8]" : "tw:bg-white",
      right && last && "tw:rounded-br-[5px]"
    )}
  >
    {children}
  </div>
);

const ValueText = ({ text = "-", secondary = "", bold = false }) => (
  <div className="tw:flex tw:flex-col tw:gap-[2px] tw:w-full tw:h-full tw:!justify-center">
    <div className={cn("tw:text-[14px] tw:sm:text-[15px] tw:leading-[20px] tw:sm:leading-[22px] tw:text-[#282828]", bold && "tw:font-[700]")}>{text || "-"}</div>
    {secondary ? <div className="tw:text-[11px] tw:sm:text-[13px] tw:leading-[16px] tw:sm:leading-[18px] tw:text-[#7C7B7B]">{secondary}</div> : null}
  </div>
);

const ValuePaxText = ({ lines = [], bold = false }) => (
  <div className="tw:flex tw:flex-col tw:gap-[2px]">
    {lines.length ? (
      lines.map((line, idx) => (
        <div
          key={`${line}-${idx}`}
          className={cn("tw:text-[14px] tw:sm:text-[15px] tw:leading-[20px] tw:sm:leading-[22px] tw:text-[#282828]", bold && "tw:font-[700]")}
        >
          {line}
        </div>
      ))
    ) : (
      <div className="tw:text-[14px] tw:sm:text-[15px] tw:leading-[20px] tw:sm:leading-[22px] tw:text-[#8D8D8D]">-</div>
    )}
  </div>
);

const VehicleInfoCell = ({ vehicleInfo, bold = false }) => {
  const { t } = useTranslation();
  const formatVehicleTagName = (vehicleName) => {
    if (!vehicleName) return "-";

    const vehicleTypeKey = String(vehicleName).trim().replace(/\s+/g, ".");
    if (!vehicleTypeKey) return "-";

    const vehicleTypeTranslations = t("VehicleTypeName", { returnObjects: true }) || {};
    const groupedTag = vehicleTypeTranslations?.[`${vehicleTypeKey}.Tag`];
    if (groupedTag) return String(groupedTag).toUpperCase();

    const translatedTag = t(`${vehicleTypeKey}.Tag`, {
      defaultValue: t(`VehicleTypeName.${vehicleTypeKey}.Tag`, {
        defaultValue: "-",
      }),
    });

    return translatedTag ? String(translatedTag).toUpperCase() : "-";
  };

  const formatVehicleTypeName = (vehicleName) => {
    if (!vehicleName) return "-";

    const vehicleTypeKey = String(vehicleName).trim().replace(/\s+/g, ".");
    if (!vehicleTypeKey) return "-";

    const vehicleTypeTranslations = t("VehicleTypeName", { returnObjects: true }) || {};
    const groupedName = vehicleTypeTranslations?.[vehicleTypeKey];
    if (groupedName) return String(groupedName);

    return t(vehicleTypeKey, {
      defaultValue: t(`VehicleTypeName.${vehicleTypeKey}`, {
        defaultValue: vehicleName || "-",
      }),
    });
  };

  if (!vehicleInfo) return null;

  const type = formatVehicleTypeName(vehicleInfo?.vehicleTypeName) || "-";
  const tag = formatVehicleTagName(vehicleInfo?.vehicleTypeName) || "-";
  const image = vehicleInfo?.vehicleTypeID?.images?.png;

  return (
    <div className="tw:flex tw:flex-col tw:lg:flex-row tw:items-start tw:lg:items-center tw:justify-between tw:gap-3">
      <div className="tw:flex tw:flex-col tw:gap-[2px]">
        <div className="tw:text-[10px] tw:leading-[14px] tw:tracking-[0.2em] tw:text-[#A7A7A7]">{tag}</div>
        <div className={cn("tw:text-[14px] tw:sm:text-[15px] tw:leading-[20px] tw:sm:leading-[22px] tw:text-[#282828]", bold && "tw:font-[700]")}>{type}</div>
        {vehicleInfo?.vehicleTypeName === "Toyota Alphard" && (
          <p className="tw:!m-0 tw:!text-[12px] tw:!font-[400] tw:!text-[#8D8D8D]">
            {t("editBooking.BookingReviewPage.vehicleSelection.alphardNote")}
          </p>
        )}
      </div>
      {image ? <img src={image} alt={type} className="tw:h-[46px] tw:w-auto tw:object-contain" /> : null}
    </div>
  );
};

const PriceDetailsCell = ({ details = [], highlight = false }) => (
  <div className="tw:flex tw:flex-col tw:gap-1">
    {details.map((item, idx) => (
      <div key={`${item.label}-${idx}`} className="tw:flex tw:items-center tw:justify-between tw:gap-1 tw:lg:gap-3">
        <div className={cn(
          "tw:w-full tw:text-[13px] tw:sm:text-[14px] tw:leading-[18px] tw:sm:leading-[20px]",
          (item.type && item.type === "discount") ? "tw:text-[#EA4335]" : "tw:text-[#8d8d8d]"
        )}>
          {item.label}
        </div>
        <div className={cn(
          "tw:w-full tw:text-[13px] tw:sm:text-[14px] tw:leading-[18px] tw:sm:leading-[20px] tw:text-right",
          item.type === "discount"
            ? "tw:text-[#EA4335]"
            : highlight ? "tw:text-[#282828] tw:font-[700]"
              : item.value === "-" || item.value === "0 JPY" || item.value === 0
                ? "tw:text-[#8d8d8d]"
                : "tw:text-[#282828]"
        )}>
          {item.value}
        </div>
      </div>
    ))}
  </div >
);

const ComparisonRow = ({ label, before, after, changed = false, beforeMuted = false, afterMuted = false }) => (
  <div className="tw:flex tw:w-full">
    <LabelCell label={label} />
    <ValueCell muted={beforeMuted}>{before}</ValueCell>
    <ValueCell right changed={changed} muted={afterMuted}>{after}</ValueCell>
  </div>
);

const BookingDetailModificationHistoryDetailPage = () => {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const getTypographyClass = createTypographyHelper(i18n.language);
  const location = useLocation();
  const { id, requestId } = useParams();
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  const { loading, authDetailBooking } = useSelector((state) => state.authBooking);

  const isJapanese = ["jp", "ja"].includes((i18n.language || "").toLowerCase());
  // const baseFontClass = isJapanese ? "tw:font-libre-caslon-display" : "tw:font-hanken-grotesk";

  useEffect(() => {
    if (!id) return;
    dispatch(newGetBookings({ id }, "detail"));
  }, [dispatch, id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const requestRows = Array.isArray(authDetailBooking?.requestChangeBooking) ? authDetailBooking.requestChangeBooking : [];
  const selectedRequest = requestRows.find((item) => String(item?._id) === String(requestId)) || location?.state?.historyItem || null;
  const statusValue = String(selectedRequest?.status || "UPDATED").toUpperCase();
  const useCurrentBookingAsAfter = statusValue === "APPROVED" || statusValue === "REJECTED";

  const previousBookingData = selectedRequest?.previousBooking || selectedRequest?.previousData || {};
  const requestedBookingData = selectedRequest?.editBooking || selectedRequest || {};
  const currentBookingData = authDetailBooking || {};
  // const editFeeCalculation = selectedRequest?.cancellationFee || {};
  const bookingPaymentStatus = selectedRequest?.paymentStatus || currentBookingData?.paymentStatus || previousBookingData?.paymentStatus || "";
  const userRole = selectedRequest?.userRole || currentBookingData?.userRole || "individual";

  const oldBooking = previousBookingData;
  const calculatedEditBooking = useCurrentBookingAsAfter ? currentBookingData : requestedBookingData;
  const changes = selectedRequest?.changesIdentification || {};

  if (loading || !authDetailBooking || !selectedRequest) {
    return (
      <FrontLayout>
        <SkeletonLoading />
      </FrontLayout>
    );
  }

  const getChangeData = (key) => {
    const item = changes?.[key] || {};
    const oldRaw = oldBooking?.[key];
    const newRaw = calculatedEditBooking?.[key];
    const oldValue = item?.old !== undefined ? item.old : oldRaw;
    const newValue = item?.new !== undefined ? item.new : newRaw;
    const computedChanged = isChangedByKey(key, oldValue, newValue);

    return {
      oldValue,
      newValue,
      changed: useCurrentBookingAsAfter ? computedChanged : (typeof item?.changed === "boolean" ? item.changed : computedChanged),
    };
  };

  const bookingTypeChange = getChangeData("bookingType");
  const paxChange = getChangeData("pax");
  const luggageChange = getChangeData("luggage");
  const pickupLocationChange = getChangeData("pickupLocation");
  const destinationsChange = getChangeData("destinations");
  const durationChange = getChangeData("duration");
  const billedDurationChange = getChangeData("billedDuration");
  const travelAreaChange = getChangeData("travelArea");
  const pickupDateTimeChange = getChangeData("pickupDateTime");
  const customerDropoffDateTimeChange = getChangeData("customerDropoffDateTime");
  const pickupFlightNoChange = getChangeData("pickupFlightNo");
  const pickupTerminalChange = getChangeData("pickupTerminal");
  const pickupSignHonorificChange = getChangeData("pickupSignHonorific");
  const pickupSignChange = getChangeData("pickupSign");
  const dropoffFlightNoChange = getChangeData("dropoffFlightNo");
  const dropoffTerminalChange = getChangeData("dropoffTerminal");
  const meetAssistChange = getChangeData("meetAssist");
  const bookingInfoChange = getChangeData("bookingInfo");
  const specialRequestChange = getChangeData("message");
  const passengerNameChange = getChangeData("passengerName");
  const passengerTitleChange = getChangeData("passengerPronounced");
  const passengerPrefixChange = getChangeData("passengerPrefixNumber");
  const passengerPhoneChange = getChangeData("passengerPhoneNumber");
  const totalPriceChange = getChangeData("totalPrice");

  const beforeVehicles = Array.isArray(bookingInfoChange?.oldValue) ? bookingInfoChange.oldValue : [];
  const afterVehicles = Array.isArray(bookingInfoChange?.newValue) ? bookingInfoChange.newValue : [];
  const maxVehicles = Math.max(beforeVehicles.length, afterVehicles.length, 1);

  const vehiclePairs = (() => {
    const pairs = [];
    const usedAfterIndices = new Set();

    for (let i = 0; i < beforeVehicles.length; i++) {
      const vBefore = beforeVehicles[i];
      const beforeTypeName = vBefore?.vehicleTypeName || vBefore?.vehicleTypeID?.typename || "";
      const matchIndex = afterVehicles.findIndex((vAfter, j) => {
        if (usedAfterIndices.has(j)) return false;
        const afterTypeName = vAfter?.vehicleTypeName || vAfter?.vehicleTypeID?.typename || "";
        return beforeTypeName && beforeTypeName === afterTypeName;
      });
      if (matchIndex !== -1) {
        usedAfterIndices.add(matchIndex);
        pairs.push({ before: vBefore, after: afterVehicles[matchIndex] });
      } else {
        pairs.push({ before: vBefore, after: null });
      }
    }

    // Second pass: pair unmatched "before" entries with leftover "after" vehicles positionally
    const unmatchedAfter = afterVehicles.filter((_, j) => !usedAfterIndices.has(j));
    let unmatchedIdx = 0;
    for (let i = 0; i < pairs.length; i++) {
      if (pairs[i].after === null && unmatchedIdx < unmatchedAfter.length) {
        pairs[i].after = unmatchedAfter[unmatchedIdx++];
      }
    }
    while (unmatchedIdx < unmatchedAfter.length) {
      pairs.push({ before: null, after: unmatchedAfter[unmatchedIdx++] });
    }

    return pairs;
  })();

  const oldPassengerName = passengerNameChange?.oldValue ?? oldBooking?.passengerName;
  const newPassengerName = passengerNameChange?.newValue ?? calculatedEditBooking?.passengerName;
  const oldPassengerTitle = passengerTitleChange?.oldValue ?? oldBooking?.passengerPronounced;
  const newPassengerTitle = passengerTitleChange?.newValue ?? calculatedEditBooking?.passengerPronounced;
  const oldPassengerPrefix = passengerPrefixChange?.oldValue ?? oldBooking?.passengerPrefixNumber;
  const newPassengerPrefix = passengerPrefixChange?.newValue ?? calculatedEditBooking?.passengerPrefixNumber;
  const oldPassengerPhone = passengerPhoneChange?.oldValue ?? oldBooking?.passengerPhoneNumber;
  const newPassengerPhone = passengerPhoneChange?.newValue ?? calculatedEditBooking?.passengerPhoneNumber;

  const formatPassengerName = (title, name) => {
    const fullName = `${title || ""} ${name || ""}`.trim();
    return fullName || "-";
  };

  const formatPassengerPhone = (prefix, phone) => {
    if (!phone) return "-";
    return `(${prefix || "-"}) ${phone}`;
  };

  const transferTypeLabel = (value, isAirportTransfer) => {
    if (value === "byTheHour") return t("editBooking.BookingReviewPage.generalInformation.bookingType.byTheHour");
    if (value === "specifiedDestination") {
      return isAirportTransfer
        ? t("editBooking.BookingReviewPage.generalInformation.bookingType.airportTransfer")
        : t("editBooking.BookingReviewPage.generalInformation.bookingType.specifiedDestination");
    }
    return "-";
  };

  const formatVehiclePriceDetails = (vehicle) => {
    if (!vehicle) return [];
    const priceDetails = [
      { label: t("editBooking.BookingReviewPage.pricing.charterCost"), value: formatJPY(vehicle.total, t) },
      { label: t("editBooking.BookingReviewPage.pricing.lateNightFee"), value: formatJPY(vehicle.nightPrice, t) },
    ];

    if (vehicle.discount?.amount > 0) {
      if (vehicle.discount?.campaignEligible) {
        priceDetails.push({
          label: t("editBooking.BookingReviewPage.pricing.campaignDiscount", { percentage: vehicle.discount?.rate }),
          type: "discount",
          value: `-${formatJPY(vehicle.discount?.amount, t)}`
        });
      } else if (vehicle.discount?.iataEligible) {
        priceDetails.push({
          label: t("editBooking.BookingReviewPage.pricing.commissionDiscount", { percentage: vehicle.discount?.rate }),
          type: "discount",
          value: `-${formatJPY(vehicle.discount?.amount, t)}`
        });
      }
    }

    priceDetails.push(
      { label: t("editBooking.BookingReviewPage.pricing.tollFee"), value: formatJPY((vehicle.tollFare || 0) + (vehicle.travelAreaPrice || 0), t) },
      { label: t("editBooking.BookingReviewPage.pricing.longDistanceSurcharge"), value: formatJPY(vehicle.longDistanceSurcharge, t) },
      { label: t("editBooking.BookingReviewPage.pricing.addOn"), value: formatJPY(vehicle.childSeatPrice, t) }
    );

    return priceDetails;
  };

  const getVehicleAddonLines = (vehicle) => {
    if (!vehicle || !Array.isArray(vehicle?.childSeat)) return [];

    const getChildSeatTypeLabel = (type, count) => {
      const typeKey = String(type || "").toLowerCase();
      return t(`editBooking.BookingReviewPage.additionalInformation.childSeatType.${typeKey}`, {
        count,
        defaultValue: `${type || "-"} x${count ?? 0}`,
      });
    };

    const addons = vehicle.childSeat
      .filter((seat) => Number(seat?.childSeatCount || 0) > 0)
      .map((seat) => getChildSeatTypeLabel(seat?.childSeatType, seat?.childSeatCount));
    return addons.length > 0 ? addons : [t("editBooking.BookingReviewPage.none")];
  };

  const formatMeetAssist = (meetAssistList = []) => {
    const arrival = meetAssistList.find((item) => item?.meetAssistType === "arrival")?.meetAssistName;
    const departure = meetAssistList.find((item) => item?.meetAssistType === "departure")?.meetAssistName;

    const hasArrival = Boolean(arrival && arrival !== "none");
    const hasDeparture = Boolean(departure && departure !== "none");

    if (!hasArrival && !hasDeparture) {
      return { title: t("editBooking.BookingReviewPage.none") };
    }

    let text = "";
    if (hasArrival) {
      text += `${t(`editBooking.BookingReviewPage.additionalInformation.meetAssistType.${arrival}`)} (${t(`editBooking.BookingReviewPage.additionalInformation.meetAssistLoc.arrival`)})`;
    }
    if (hasDeparture) {
      if (text) text += "\n";
      text += `${t(`editBooking.BookingReviewPage.additionalInformation.meetAssistType.${departure}`)} (${t(`editBooking.BookingReviewPage.additionalInformation.meetAssistLoc.departure`)})`;
    }

    return {
      title: text,
    };
  };

  const formatMessage = (message) => {
    if (!message) return "-";
    if (Array.isArray(message)) return message?.[0]?.note || "-";
    return String(message || "").trim() || "-";
  };

  const getPickupSignText = (honorific, name) => {
    if (!name) return "-";
    if (honorific === "-") return name;
    return `${honorific || ""} ${name || ""}`.trim();
  };

  const getOldBookingPaymentInfo = (booking, deferredPayment) => {
    if (deferredPayment) {
      if (booking?.paymentStatus !== "CHARGE_PENDING") {
        if (booking?.confirmWithEditedFee) {
          return t("editBooking.BookingReviewPage.pricing.addedToMonthlyInvoiceWithEditedFee");
        }
        return t("editBooking.BookingReviewPage.pricing.addedToMonthlyInvoice");
      }
    } else {
      if (booking?.paymentStatus !== "CHARGE_PENDING") {
        if (booking?.editedFeeType === "CHARGE_EDITED_FEE") {
          return t("editBooking.BookingReviewPage.pricing.chargedWithEditedFee");
        }
        return t("editBooking.BookingReviewPage.pricing.charged");
      }
    }

    return t("editBooking.BookingReviewPage.pricing.notCharged");
  };

  const statusMeta = getStatusMeta(statusValue, selectedRequest?.responseBy);
  const statusLabel = isJapanese ? statusMeta?.labels?.ja || statusMeta?.labels?.en : statusMeta?.labels?.en;
  const headerDate = formatDateTimeLocal(selectedRequest?.date || selectedRequest?.createdAt || selectedRequest?.updatedAt, i18n.language, { withWeekday: false });
  const passengerText = authDetailBooking?.customer?.name || "Passenger";
  const bookingIdLabel = t("my_page.bookings_page.booking_item.booking_id");
  const headerSubtitle = isJapanese
    ? `${passengerText} • ${bookingIdLabel} #${authDetailBooking?.key || "-"}`
    : `For ${passengerText} • ${bookingIdLabel} #${authDetailBooking?.key || "-"}`;
  const totalMeetAssistPriceChange = getChangeData("totalMeetAssistPrice");

  const pickupFlightLabelType = (() => {
    const fromAirport = oldBooking?.isFromAirport || calculatedEditBooking?.isFromAirport;
    const fromStation = oldBooking?.isFromStation || calculatedEditBooking?.isFromStation;
    if (fromAirport && fromStation) return "both";
    if (fromStation) return "station";
    return "airport";
  })();

  const dropoffFlightLabelType = (() => {
    const toAirport = oldBooking?.isToAirport || calculatedEditBooking?.isToAirport;
    const toStation = oldBooking?.isToStation || calculatedEditBooking?.isToStation;
    if (toAirport && toStation) return "both";
    if (toStation) return "station";
    return "airport";
  })();

  const showTransferAdditionalSection = Boolean(
    oldBooking?.isAirportTransfer ||
    calculatedEditBooking?.isAirportTransfer ||
    oldBooking?.isStationTransfer ||
    calculatedEditBooking?.isStationTransfer
  );

  const userDeferredPaymentEligible = Boolean(
    currentBookingData?.deferredPayment ||
    currentBookingData?.user?.deferredPaymentEligible
  );
  const beforePrice = Number(totalPriceChange?.oldValue || 0);
  const afterPrice = Number(totalPriceChange?.newValue || 0);
  const diffPrice = afterPrice - beforePrice;

  const formatVehicleTagName = (vehicleName) => {
    if (!vehicleName) return "-";

    const vehicleTypeKey = String(vehicleName).trim().replace(/\s+/g, ".");
    if (!vehicleTypeKey) return "-";

    const vehicleTypeTranslations = t("VehicleTypeName", { returnObjects: true }) || {};
    const groupedTag = vehicleTypeTranslations?.[`${vehicleTypeKey}.Tag`];
    if (groupedTag) return String(groupedTag).toUpperCase();

    const translatedTag = t(`${vehicleTypeKey}.Tag`, {
      defaultValue: t(`VehicleTypeName.${vehicleTypeKey}.Tag`, {
        defaultValue: "-",
      }),
    });

    return translatedTag ? String(translatedTag).toUpperCase() : "-";
  };

  const formatVehicleTypeName = (vehicleName) => {
    if (!vehicleName) return "-";

    const vehicleTypeKey = String(vehicleName).trim().replace(/\s+/g, ".");
    if (!vehicleTypeKey) return "-";

    const vehicleTypeTranslations = t("VehicleTypeName", { returnObjects: true }) || {};
    const groupedName = vehicleTypeTranslations?.[vehicleTypeKey];
    if (groupedName) return String(groupedName);

    return t(vehicleTypeKey, {
      defaultValue: t(`VehicleTypeName.${vehicleTypeKey}`, {
        defaultValue: vehicleName || "-",
      }),
    });
  };


  return (
    <FrontLayout>
      <div className={cn("tw:bg-[linear-gradient(180deg,#f6f6f6_0%,#ffffff_100%)] tw:pt-10 tw:pb-16 tw:text-[#212529] tw:font-hanken-grotesk")}>
        <div className="tw:max-w-[1300px] tw:mx-auto tw:px-0 tw:lg:px-6">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-6 tw:px-5 tw:lg:px-0 tw:pb-8">
            <Link
              to={`/booking-detail/${id}/modification-history`}
              className="tw:inline-flex tw:items-center tw:gap-[10px] tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-[#e8e8e8] tw:px-3 tw:py-2 tw:text-[#282828] tw:!no-underline"
            >
              <BackIcon color="#8D8D8D" width={7} height={10} />
              <span className="tw:text-[15px] tw:leading-[22px] tw:text-[#282828]">{t("Back")}</span>
            </Link>

            <div className="tw:flex tw:flex-col tw:gap-[4px]">
              <div className="tw:text-[15px] tw:font-[500] tw:leading-[22px] tw:text-[#8D8D8D]">
                {headerSubtitle}
              </div>
              <div className="tw:text-[22px] tw:sm:text-[24px] tw:font-[600] tw:leading-[30px] tw:sm:leading-normal tw:text-[#282828]">
                {`${headerDate}・Log`}
              </div>
            </div>
          </div>

          <div className="tw:px-0 tw:sm:px-5 tw:lg:px-0">
            <section className="tw:w-full tw:overflow-hidden tw:rounded-[5px] tw:border-r tw:border-[#E8E8E8]">
              <div className="tw:flex tw:w-full">
                <div className="tw:w-[90px] tw:sm:w-[225px] tw:h-[68px] tw:bg-white tw:border-l tw:border-t tw:border-[#E8E8E8] tw:flex tw:items-center tw:justify-center" >
                  <div className={cn("tw:inline-flex tw:min-w-[126px] tw:h-9 tw:items-center tw:justify-center tw:rounded-[5px] tw:px-6 tw:py-[8px] tw:text-[14px] tw:leading-[18px]", statusMeta.className)}>
                    {statusLabel}
                  </div>
                </div>
                <div className="tw:flex-1 tw:h-[68px] tw:bg-[#282828] tw:border-t tw:border-b tw:border-[#f9f9f9] tw:px-2 tw:sm:px-5 tw:flex tw:items-center tw:gap-3">
                  <div className="tw:text-white tw:text-[13px] tw:sm:text-[15px] tw:leading-[18px] tw:sm:leading-[22px] tw:font-[700]">{t("editBooking.BookingReviewPage.before")}</div>
                </div>
                <div className="tw:flex-1 tw:h-[68px] tw:bg-[#282828] tw:border-t tw:border-b tw:border-l tw:border-[#f9f9f9] tw:px-2 tw:sm:px-5 tw:flex tw:items-center tw:rounded-tr-[5px]">
                  <div className="tw:text-white tw:text-[13px] tw:sm:text-[15px] tw:leading-[18px] tw:sm:leading-[22px] tw:font-[700]">{t("editBooking.BookingReviewPage.after")}</div>
                </div>
              </div>

              <SectionTitleRow title={t("editBooking.BookingReviewPage.generalInformation.title")} />

              <ComparisonRow
                label={t("editBooking.BookingReviewPage.generalInformation.transferType")}
                before={<ValueText text={transferTypeLabel(bookingTypeChange?.oldValue, oldBooking?.isAirportTransfer)} />}
                after={<ValueText text={transferTypeLabel(bookingTypeChange?.newValue, calculatedEditBooking?.isAirportTransfer)} bold={bookingTypeChange?.changed} />}
                changed={bookingTypeChange?.changed}
              />

              <ComparisonRow
                label={t("editBooking.BookingReviewPage.generalInformation.paxAndLuggage")}
                before={<ValuePaxText lines={[formatPaxGroup(paxChange?.oldValue, t), formatLuggageGroup(luggageChange?.oldValue, t)]} />}
                after={<ValuePaxText lines={[formatPaxGroup(paxChange?.newValue, t), formatLuggageGroup(luggageChange?.newValue, t)]} bold={paxChange?.changed || luggageChange?.changed} />}
                changed={paxChange?.changed || luggageChange?.changed}
              />

              <ComparisonRow
                label={t("editBooking.BookingReviewPage.generalInformation.pickupLocation")}
                before={<ValueText text={getLocationDetail(pickupLocationChange?.oldValue).name} secondary={getLocationDetail(pickupLocationChange?.oldValue).address} />}
                after={<ValueText text={getLocationDetail(pickupLocationChange?.newValue).name} secondary={getLocationDetail(pickupLocationChange?.newValue).address} bold={pickupLocationChange?.changed} />}
                changed={pickupLocationChange?.changed}
              />

              <div className="tw:flex tw:w-full">
                <LabelCell label={t("editBooking.BookingReviewPage.generalInformation.dropoffLocation")} />
                <div className="tw:flex tw:flex-col tw:flex-1">
                  {(() => {
                    const oldDests = Array.isArray(destinationsChange?.oldValue) ? destinationsChange.oldValue : [];
                    const newDests = Array.isArray(destinationsChange?.newValue) ? destinationsChange.newValue : [];
                    const destinationRowsChanged = destinationsChange?.changed;
                    const rowChanged = useCurrentBookingAsAfter ? destinationRowsChanged : destinationsChange?.changed;
                    const maxRows = Math.max(oldDests.length, newDests.length, 1);

                    return Array.from({ length: maxRows }).map((_, idx) => {
                      const pair = {
                        old: oldDests[idx] || null,
                        new: newDests[idx] || null,
                      };
                      const oldLocation = pair.old ? getLocationDetail(pair.old) : null;
                      const newLocation = pair.new ? getLocationDetail(pair.new) : null;

                      return (
                        <div key={`dest-${idx}`} className="tw:flex tw:w-full">
                          <ValueCell muted={!pair.old}>
                            {pair.old ? <ValueText text={oldLocation.name} secondary={oldLocation.address} /> : <ValueText text="-" />}
                          </ValueCell>
                          <ValueCell right changed={rowChanged && !!pair.new} muted={!pair.new}>
                            {pair.new ? <ValueText text={newLocation.name} secondary={newLocation.address} bold={rowChanged} /> : <ValueText text="-" />}
                          </ValueCell>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {(bookingTypeChange?.oldValue === "byTheHour" || bookingTypeChange?.newValue === "byTheHour") && (
                <>
                  <ComparisonRow
                    label={t("editBooking.BookingReviewPage.generalInformation.usageDuration")}
                    before={<ValueText text={formatDurationText(durationChange?.oldValue, t)} secondary={bookingTypeChange?.oldValue === "byTheHour" ? `${t("editBooking.BookingReviewPage.generalInformation.billedDuration")}: ${formatBilledHoursText(billedDurationChange?.oldValue, t)}` : undefined} />}
                    after={<ValueText text={formatDurationText(durationChange?.newValue, t)} secondary={bookingTypeChange?.newValue === "byTheHour" ? `${t("editBooking.BookingReviewPage.generalInformation.billedDuration")}: ${formatBilledHoursText(billedDurationChange?.newValue, t)}` : undefined} bold={durationChange?.changed} />}
                    changed={durationChange?.changed}
                  />

                  <ComparisonRow
                    label={t("editBooking.BookingReviewPage.generalInformation.travelArea")}
                    before={<ValueText text={travelAreaChange?.oldValue ? t(`myPage.booking.bookingItem.travelArea.${travelAreaChange.oldValue}`) : "-"} />}
                    after={<ValueText text={travelAreaChange?.newValue ? t(`myPage.booking.bookingItem.travelArea.${travelAreaChange.newValue}`) : "-"} bold={travelAreaChange?.changed} />}
                    changed={travelAreaChange?.changed}
                  />
                </>
              )}

              <ComparisonRow
                label={t("editBooking.BookingReviewPage.generalInformation.pickupTime")}
                before={<ValueText text={formatDateTime(pickupDateTimeChange?.oldValue, i18n.language)} />}
                after={<ValueText text={formatDateTime(pickupDateTimeChange?.newValue, i18n.language)} bold={pickupDateTimeChange?.changed} />}
                changed={pickupDateTimeChange?.changed}
              />

              <ComparisonRow
                label={t("editBooking.BookingReviewPage.generalInformation.estDropoffTime")}
                before={<ValueText text={formatDateTime(customerDropoffDateTimeChange?.oldValue, i18n.language)} />}
                after={<ValueText text={formatDateTime(customerDropoffDateTimeChange?.newValue, i18n.language)} bold={customerDropoffDateTimeChange?.changed} />}
                changed={customerDropoffDateTimeChange?.changed}
              />

              <SectionTitleRow title={t("editBooking.BookingReviewPage.vehicleSelection.title")} />

              {vehiclePairs.map(({ before: beforeVehicle, after: afterVehicle }, index) => {
                const vehicleChanged = Boolean(beforeVehicle || afterVehicle) && !isEqualValue(
                  normalizeVehicleIdentity(beforeVehicle),
                  normalizeVehicleIdentity(afterVehicle)
                );

                return (
                  <ComparisonRow
                    key={`vehicle-${index}`}
                    label={vehiclePairs.length > 1 ? `${t("editBooking.BookingReviewPage.vehicleSelection.vehicle", { number: "" })} ${index + 1}` : t("editBooking.BookingReviewPage.vehicleSelection.vehicle", { number: "" })}
                    before={beforeVehicle ? <VehicleInfoCell vehicleInfo={beforeVehicle} /> : <ValueText text="-" />}
                    after={afterVehicle ? <VehicleInfoCell vehicleInfo={afterVehicle} bold={vehicleChanged} /> : <ValueText text="-" />}
                    changed={vehicleChanged}
                    beforeMuted={!beforeVehicle}
                    afterMuted={!afterVehicle}
                  />
                );
              })}

              <SectionTitleRow title={t("editBooking.BookingReviewPage.passengerInformation.title")} />

              <ComparisonRow
                label={t("editBooking.BookingReviewPage.passengerInformation.passengerName")}
                before={<ValueText text={formatPassengerName(oldPassengerTitle, oldPassengerName)} />}
                after={<ValueText text={formatPassengerName(newPassengerTitle, newPassengerName)} bold={passengerNameChange?.changed || passengerTitleChange?.changed} />}
                changed={passengerNameChange?.changed || passengerTitleChange?.changed}
              />

              <ComparisonRow
                label={t("editBooking.BookingReviewPage.passengerInformation.passengerPhoneNumber")}
                before={<ValueText text={formatPassengerPhone(oldPassengerPrefix, oldPassengerPhone)} />}
                after={<ValueText text={formatPassengerPhone(newPassengerPrefix, newPassengerPhone)} bold={passengerPhoneChange?.changed || passengerPrefixChange?.changed} />}
                changed={passengerPhoneChange?.changed || passengerPrefixChange?.changed}
              />

              <SectionTitleRow title={t("editBooking.BookingReviewPage.additionalInformation.title")} />

              {showTransferAdditionalSection && (
                <>
                  {(oldBooking?.isFromAirport || calculatedEditBooking?.isFromAirport || oldBooking?.isFromStation || calculatedEditBooking?.isFromStation) && (
                    <>
                      <ComparisonRow
                        label={t(`editBooking.BookingReviewPage.additionalInformation.pickupFlightNumber.${pickupFlightLabelType}`)}
                        before={<ValueText text={pickupFlightNoChange?.oldValue || "-"} />}
                        after={<ValueText text={pickupFlightNoChange?.newValue || "-"} bold={pickupFlightNoChange?.changed} />}
                        changed={pickupFlightNoChange?.changed}
                      />

                      {(oldBooking?.isFromAirport || calculatedEditBooking?.isFromAirport) && (
                        <>
                          <ComparisonRow
                            label={t("editBooking.BookingReviewPage.additionalInformation.pickupTerminal")}
                            before={<ValueText text={pickupTerminalChange?.oldValue ? t(`bookingDetail.bookingItem.terminal.${pickupTerminalChange.oldValue}`) : "-"} />}
                            after={<ValueText text={pickupTerminalChange?.newValue ? t(`bookingDetail.bookingItem.terminal.${pickupTerminalChange.newValue}`) : "-"} bold={pickupTerminalChange?.changed} />}
                            changed={pickupTerminalChange?.changed}
                          />
                          <ComparisonRow
                            label={t("editBooking.BookingReviewPage.additionalInformation.pickupSign.airport")}
                            before={<ValueText text={getPickupSignText(pickupSignHonorificChange?.oldValue, pickupSignChange?.oldValue)} />}
                            after={<ValueText text={getPickupSignText(pickupSignHonorificChange?.newValue, pickupSignChange?.newValue)} bold={pickupSignChange?.changed || pickupSignHonorificChange?.changed} />}
                            changed={pickupSignChange?.changed || pickupSignHonorificChange?.changed}
                          />
                        </>
                      )}
                    </>
                  )}

                  {(oldBooking?.isToAirport || calculatedEditBooking?.isToAirport || oldBooking?.isToStation || calculatedEditBooking?.isToStation) && (
                    <>
                      <ComparisonRow
                        label={t(`editBooking.BookingReviewPage.additionalInformation.dropoffFlightNumber.${dropoffFlightLabelType}`)}
                        before={<ValueText text={dropoffFlightNoChange?.oldValue || "-"} />}
                        after={<ValueText text={dropoffFlightNoChange?.newValue || "-"} bold={dropoffFlightNoChange?.changed} />}
                        changed={dropoffFlightNoChange?.changed}
                      />

                      {(oldBooking?.isToAirport || calculatedEditBooking?.isToAirport) && (
                        <ComparisonRow
                          label={t("editBooking.BookingReviewPage.additionalInformation.dropoffTerminal")}
                          before={<ValueText text={dropoffTerminalChange?.oldValue ? t(`bookingDetail.bookingItem.terminal.${dropoffTerminalChange.oldValue}`) : "-"} />}
                          after={<ValueText text={dropoffTerminalChange?.newValue ? t(`bookingDetail.bookingItem.terminal.${dropoffTerminalChange.newValue}`) : "-"} bold={dropoffTerminalChange?.changed} />}
                          changed={dropoffTerminalChange?.changed}
                        />
                      )}
                    </>
                  )}

                  <ComparisonRow
                    label={t("editBooking.BookingReviewPage.additionalInformation.meetAssistService")}
                    before={<ValueText text={formatMeetAssist(meetAssistChange?.oldValue).title} />}
                    after={<ValueText text={formatMeetAssist(meetAssistChange?.newValue).title} bold={meetAssistChange?.changed} />}
                    changed={meetAssistChange?.changed}
                  />
                </>
              )}

              <div className="tw:flex tw:w-full">
                <LabelCell label={t("editBooking.BookingReviewPage.additionalInformation.addOnServices")} />
                <div className="tw:flex tw:flex-col tw:flex-1">
                  {vehiclePairs.map(({ before: beforeVehicle, after: afterVehicle }, index) => {
                    const showVehicleLabel = vehiclePairs.length > 1;
                    const addonChanged = !isEqualValue(
                      normalizeVehicleAddon(beforeVehicle),
                      normalizeVehicleAddon(afterVehicle)
                    );

                    return (
                      <React.Fragment key={`addon-${index}`}>
                        <div className="tw:grid tw:grid-cols-2 tw:items-stretch tw:lg:hidden tw:h-full">
                          {showVehicleLabel && (
                            <>
                              <div className={cn("tw:flex tw:items-center tw:border-l tw:border-b tw:border-[#e8e8e8] tw:px-3 tw:py-2 tw:text-[14px] tw:font-[400] tw:break-keep", beforeVehicle ? "tw:bg-[#f9f9f9]" : "tw:bg-[#f3f3f3]")}>
                                {beforeVehicle ? formatVehicleTypeName(beforeVehicle?.vehicleTypeName) : "-"}
                              </div>
                              <div className={cn("tw:flex tw:items-center tw:border-l tw:border-b tw:border-[#e8e8e8] tw:px-3 tw:py-2 tw:text-[14px] tw:font-[400] tw:break-keep", afterVehicle ? (addonChanged ? "tw:bg-[#FFF8E8]" : "tw:bg-[#f9f9f9]") : "tw:bg-[#f3f3f3]")}>
                                {afterVehicle ? formatVehicleTypeName(afterVehicle?.vehicleTypeName) : "-"}
                              </div>
                            </>
                          )}

                          <div className={cn("tw:border-l tw:border-b tw:border-[#e8e8e8] tw:px-2 tw:py-4 tw:flex tw:items-center", beforeVehicle ? "tw:bg-white" : "tw:bg-[#F3F3F3]")}>
                            {beforeVehicle ? <ValuePaxText lines={getVehicleAddonLines(beforeVehicle)} /> : <ValueText text="-" />}
                          </div>

                          <div className={cn("tw:border-l tw:border-b tw:border-[#e8e8e8] tw:px-2 tw:py-4 tw:flex tw:items-center", afterVehicle ? (addonChanged ? "tw:bg-[#FFF8E8]" : "tw:bg-white") : "tw:bg-[#F3F3F3]")}>
                            {afterVehicle ? <ValuePaxText lines={getVehicleAddonLines(afterVehicle)} bold={addonChanged && !!afterVehicle} /> : <ValueText text="-" />}
                          </div>
                        </div>

                        <div className="tw:hidden tw:lg:flex tw:flex-row tw:w-full tw:items-stretch">
                          <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1">
                            {showVehicleLabel && (
                              <div className={cn(
                                "tw:w-full tw:lg:w-[160px] tw:lg:px-5 tw:flex tw:h-full tw:items-center tw:p-2 tw:border-b tw:lg:border-r tw:border-l tw:border-[#e8e8e8]",
                                beforeVehicle ? "tw:bg-[#f9f9f9]" : "tw:bg-[#f3f3f3]"
                              )}>
                                <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-[400] tw:break-keep">
                                  {beforeVehicle ? formatVehicleTypeName(beforeVehicle?.vehicleTypeName) : "-"}
                                </div>
                              </div>
                            )}
                            <div className="tw:flex-1 tw:flex tw:h-full">
                              <ValueCell muted={!beforeVehicle}>
                                {beforeVehicle && <ValuePaxText lines={getVehicleAddonLines(beforeVehicle)} />}
                              </ValueCell>
                            </div>
                          </div>
                          <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1">
                            {showVehicleLabel && (
                              <div className={cn(
                                "tw:w-full tw:lg:w-[160px] tw:lg:px-5 tw:flex tw:h-full tw:items-center tw:p-2 tw:border-b tw:lg:border-r tw:border-l tw:border-[#e8e8e8]",
                                afterVehicle ? (addonChanged ? "tw:bg-[#FFF8E8]" : "tw:bg-[#f9f9f9]") : "tw:bg-[#f3f3f3]"
                              )}>
                                <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-[400] tw:break-keep">
                                  {afterVehicle ? formatVehicleTypeName(afterVehicle?.vehicleTypeName) : "-"}
                                </div>
                              </div>
                            )}
                            <div className="tw:flex-1 tw:flex tw:h-full">
                              <ValueCell right changed={addonChanged && !!afterVehicle} muted={!afterVehicle}>
                                {afterVehicle && <ValuePaxText lines={getVehicleAddonLines(afterVehicle)} bold={addonChanged && !!afterVehicle} />}
                              </ValueCell>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              <ComparisonRow
                label={t("editBooking.BookingReviewPage.additionalInformation.specialRequest")}
                before={<ValueText text={formatMessage(specialRequestChange?.oldValue)} />}
                after={<ValueText text={formatMessage(specialRequestChange?.newValue)} bold={specialRequestChange?.changed} />}
                changed={specialRequestChange?.changed}
              />

              <SectionTitleRow title={t("editBooking.BookingReviewPage.pricing.title")} />

              <div className="tw:flex tw:w-full">
                <LabelCell label={t("editBooking.BookingReviewPage.pricing.detail")} />
                <div className="tw:flex tw:flex-col tw:flex-1">
                  {vehiclePairs.map(({ before: beforeVehicle, after: afterVehicle }, index) => {
                    const showVehicleLabel = vehiclePairs.length > 1;
                    const changed = Boolean(beforeVehicle || afterVehicle) && !isEqualValue(
                      normalizeVehiclePricing(beforeVehicle),
                      normalizeVehiclePricing(afterVehicle)
                    );

                    return (
                      <React.Fragment key={`price-${index}`}>
                        <div className="tw:grid tw:grid-cols-2 tw:items-stretch tw:lg:hidden tw:h-full">
                          {showVehicleLabel && (
                            <>
                              <div className={cn("tw:flex tw:items-center tw:border-l tw:border-b tw:border-[#e8e8e8] tw:px-3 tw:py-2 tw:text-[14px] tw:font-[400] tw:break-keep", beforeVehicle ? "tw:bg-[#f9f9f9]" : "tw:bg-[#f3f3f3]")}>
                                {beforeVehicle ? formatVehicleTypeName(beforeVehicle?.vehicleTypeName) : "-"}
                              </div>
                              <div className={cn("tw:flex tw:items-center tw:border-l tw:border-b tw:border-[#e8e8e8] tw:px-3 tw:py-2 tw:text-[14px] tw:font-[400] tw:break-keep", afterVehicle ? (changed ? "tw:bg-[#FFF8E8]" : "tw:bg-[#f9f9f9]") : "tw:bg-[#f3f3f3]")}>
                                {afterVehicle ? formatVehicleTypeName(afterVehicle?.vehicleTypeName) : "-"}
                              </div>
                            </>
                          )}

                          <div className={cn("tw:border-l tw:border-b tw:border-[#e8e8e8] tw:px-2 tw:py-4 tw:flex tw:items-center", beforeVehicle ? "tw:bg-white" : "tw:bg-[#F3F3F3]")}>
                            {beforeVehicle ? <PriceDetailsCell details={formatVehiclePriceDetails(beforeVehicle)} /> : <ValueText text="-" />}
                          </div>

                          <div className={cn("tw:border-l tw:border-b tw:border-[#e8e8e8] tw:px-2 tw:py-4 tw:flex tw:items-center", afterVehicle ? (changed ? "tw:bg-[#FFF8E8]" : "tw:bg-white") : "tw:bg-[#F3F3F3]")}>
                            {afterVehicle ? <PriceDetailsCell details={formatVehiclePriceDetails(afterVehicle)} highlight /> : <ValueText text="-" />}
                          </div>
                        </div>

                        <div className="tw:hidden tw:lg:flex tw:flex-row tw:w-full tw:items-stretch">
                          <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                            {showVehicleLabel && (
                              <div className={cn(
                                "tw:w-full tw:lg:w-[160px] tw:lg:px-5 tw:flex tw:h-full tw:items-center tw:p-2 tw:border-b tw:lg:border-r tw:border-l tw:border-[#e8e8e8]",
                                beforeVehicle ? "tw:bg-[#f9f9f9]" : "tw:bg-[#f3f3f3]"
                              )}>
                                <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-[400] tw:break-keep">
                                  {beforeVehicle ? formatVehicleTypeName(beforeVehicle?.vehicleTypeName) : "-"}
                                </div>
                              </div>
                            )}
                            <div className="tw:flex-1 tw:flex tw:h-full">
                              <ValueCell muted={!beforeVehicle}>
                                {beforeVehicle ? <PriceDetailsCell details={formatVehiclePriceDetails(beforeVehicle)} /> : <ValueText text="-" />}
                              </ValueCell>
                            </div>
                          </div>
                          <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                            {showVehicleLabel && (
                              <div className={cn(
                                "tw:w-full tw:lg:w-[160px] tw:lg:px-5 tw:flex tw:h-full tw:items-center tw:p-2 tw:border-b tw:lg:border-r tw:border-l tw:border-[#e8e8e8]",
                                afterVehicle ? (changed ? "tw:bg-[#FFF8E8]" : "tw:bg-[#f9f9f9]") : "tw:bg-[#f3f3f3]"
                              )}>
                                <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-[400] tw:break-keep">
                                  {afterVehicle ? formatVehicleTypeName(afterVehicle?.vehicleTypeName) : "-"}
                                </div>
                              </div>
                            )}
                            <div className="tw:flex-1 tw:flex tw:h-full">
                              <ValueCell right changed={changed} muted={!afterVehicle}>
                                {afterVehicle ? <PriceDetailsCell details={formatVehiclePriceDetails(afterVehicle)} highlight /> : <ValueText text="-" />}
                              </ValueCell>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div className="tw:flex tw:w-full">
                    <ValueCell>
                      <PriceDetailsCell details={[{ label: t("editBooking.BookingReviewPage.pricing.meetAssistService"), value: formatJPY(totalMeetAssistPriceChange?.oldValue, t) }]} />
                    </ValueCell>
                    <ValueCell right changed={totalMeetAssistPriceChange?.changed}>
                      <PriceDetailsCell details={[{ label: t("editBooking.BookingReviewPage.pricing.meetAssistService"), value: formatJPY(totalMeetAssistPriceChange?.newValue, t) }]} highlight />
                    </ValueCell>
                  </div>
                </div>
              </div>

              <div className="tw:flex tw:w-full">
                <LabelCell label={t("editBooking.BookingReviewPage.pricing.totalPrice")} first />
                <ValueCell>
                  <div className="tw:flex tw:h-full tw:items-center">
                    <div className="tw:flex tw:flex-col tw:sm:flex-row tw:items-start tw:sm:items-center tw:justify-between tw:w-full tw:gap-1 tw:sm:gap-0">
                      <div className="tw:text-[#8d8d8d] tw:text-[12px] tw:sm:text-[14px] tw:leading-[18px] tw:sm:leading-[20px] tw:italic">
                        {getOldBookingPaymentInfo(oldBooking, userDeferredPaymentEligible)}
                      </div>
                      <div className="tw:text-[14px] tw:sm:text-[16px] tw:leading-[20px] tw:sm:leading-[22px] tw:font-[500] tw:text-[#282828]">{formatJPY(totalPriceChange?.oldValue, t)}</div>
                    </div>
                  </div>
                </ValueCell>
                <ValueCell right changed={totalPriceChange?.changed} last>
                  <div className="tw:flex tw:flex-col tw:items-end tw:gap-[3px] tw:w-full">
                    <div className="tw:text-[14px] tw:sm:text-[16px] tw:leading-[20px] tw:sm:leading-[22px] tw:font-[700] tw:text-[#282828]">{formatJPY(totalPriceChange?.newValue, t)}</div>
                    {totalPriceChange?.changed && (
                      <div className="tw:text-[13px] tw:leading-[18px] tw:font-[700] tw:text-[#EA4335]">
                        {diffPrice > 0 ? "+" : ""}{formatJPY(diffPrice, t)}
                      </div>
                    )}
                  </div>
                </ValueCell>
              </div>
            </section>
            {
              (selectedRequest?.totalPriceCancellation > 0 || selectedRequest?.totalMeetAssistPriceCancellation > 0) && (
                <div className="tw:mt-[42px]">
                  <CancellationFeeInformation
                    getTypographyClass={getTypographyClass}
                    id={selectedRequest?._id}
                    editFeeCalculation={{
                      totalPriceCancellation: Number(selectedRequest?.totalPriceCancellation || 0),
                      meetAssistCancelFee: selectedRequest?.meetAssistCancellationFee || { arrival: 0, departure: 0 },
                      meetAssistCancelDesc: selectedRequest?.meetAssistCancellationDesc || { arrival: { en: "-", jp: "-" }, departure: { en: "-", jp: "-" } },
                      totalFee: Number(selectedRequest?.cancellationFee || 0),
                      desc: selectedRequest?.cancellationDesc || { en: "-", jp: "-" },
                      refundAmount: Number(selectedRequest?.refundAmount || 0),
                      totalMeetAssistCancellation: Number(selectedRequest?.totalMeetAssistPriceCancellation || 0),
                    }}
                    newBookingTotalPrice={selectedRequest.editBooking.totalPrice}
                    deferredPayment={userDeferredPaymentEligible}
                    openPolicyModal={() => setShowPolicyModal(true)}
                    changes={changes}
                    oldBookingPrice={(selectedRequest?.status === "APPROVED" || selectedRequest?.status === "REJECTED") ? oldBooking.totalPrice : currentBookingData.totalPrice}
                    meetAssistChangesIdentification={meetAssistChange}
                  />
                </div>
              )
            }
            <CancellationPolicyModal
              show={showPolicyModal}
              onClose={() => setShowPolicyModal(false)}
              role={userRole}
            />
          </div>
        </div>
      </div>
    </FrontLayout>
  );
};

export default BookingDetailModificationHistoryDetailPage;
